// The output formats each report/export save offers, in one place so the two
// builds cannot drift: the desktop feeds these to the native save dialog's
// `filters` (via `toTauriFilters`), the browser to ReportSaveDialog's format
// list. The entries and their order mirror the C++ CFileDialog filter strings —
// the FIRST entry is the default, which is also the extension the writers get
// when the user changes nothing.
//
// `labelId` is a Tiger resource id resolved at use time (so language overrides
// apply); the web dialog renders it as "<label> (.<ext>)".

import { getTigerResource } from "./tigerResource";

export interface ReportFormat {
  /** Output extension, no dot. Selects the writer in every generator. */
  ext: string;
  /** Tiger resource id for the format's name. */
  labelId: string;
}

// .pdf is offered wherever the generator can reach it through `convert_file`
// (tiger_format_convert_rs / WordFileConvert.exe, or the convert tunnel): the
// document is written as flat ODF or Word ML first, then converted.

/** Controller Values report (C++ OnApp_controller_report_button). */
export const CONTROLLER_REPORT_FORMATS: ReportFormat[] = [
  { ext: "doc", labelId: "TIGER_REPORT_FILTER_DOC" },
  { ext: "odt", labelId: "TIGER_REPORT_FILTER_ODT" },
  { ext: "pdf", labelId: "TIGER_REPORT_FILTER_PDF" },
];

/** Performance Stability report (C++ OnApp_stability_report_button). */
export const STABILITY_REPORT_FORMATS: ReportFormat[] = [
  { ext: "doc", labelId: "TIGER_REPORT_FILTER_STABILITY_DOC" },
  { ext: "odt", labelId: "TIGER_REPORT_FILTER_STABILITY_ODT" },
  { ext: "pdf", labelId: "TIGER_REPORT_FILTER_PDF" },
];

/** Event-model report (C++ event_menu_item_cls::check_and_perform): the same
 *  filter name for both extensions, as in the C++. */
export const EVENT_REPORT_FORMATS: ReportFormat[] = [
  { ext: "txt", labelId: "TIGER_REPORT_FILTER_EVENT" },
  { ext: "xls", labelId: "TIGER_REPORT_FILTER_EVENT" },
];

/** Window > Save Tools as Report. The .xml / .fodt entries produce report
 *  TEMPLATES, not documents. */
export const TOOL_REPORT_FORMATS: ReportFormat[] = [
  { ext: "doc", labelId: "TIGER_REPORT_FILTER_DOC" },
  { ext: "odt", labelId: "TIGER_REPORT_FILTER_ODT" },
  { ext: "pdf", labelId: "TIGER_REPORT_FILTER_PDF" },
  { ext: "xml", labelId: "TIGER_REPORT_FILTER_XML" },
  { ext: "fodt", labelId: "TIGER_REPORT_FILTER_FODT" },
];

/** Excel custom reports (TigerExcelReports.exe) write one format only. */
export const EXCEL_REPORT_FORMATS: ReportFormat[] = [
  { ext: "xlsx", labelId: "TIGER_REPORT_FILTER_XLSX" },
];

/** Custom XML report driven by a Word (WordprocessingML) template. .pdf is the
 *  processed .doc run through WordFileConvert. */
export const XML_REPORT_WORD_FORMATS: ReportFormat[] = [
  { ext: "doc", labelId: "TIGER_REPORT_FILTER_DOC" },
  { ext: "xml", labelId: "TIGER_REPORT_FILTER_XML" },
  { ext: "pdf", labelId: "TIGER_REPORT_FILTER_PDF" },
];

/** Custom XML report driven by a `.fodt` template: .docx / .pdf come out of
 *  `convert_file` (tiger_format_convert_rs or the convert tunnel), which runs
 *  server-side on the web build too. */
export const XML_REPORT_FODT_FORMATS: ReportFormat[] = [
  { ext: "odt", labelId: "TIGER_REPORT_FILTER_ODT" },
  { ext: "fodt", labelId: "TIGER_REPORT_FILTER_FODT" },
  { ext: "docx", labelId: "TIGER_REPORT_FILTER_DOCX" },
  { ext: "pdf", labelId: "TIGER_REPORT_FILTER_PDF" },
];

/** Which XML-report format list a template file gets. */
export function xmlReportFormats(reportFile: string): ReportFormat[] {
  return /\.fodt$/i.test(reportFile) ? XML_REPORT_FODT_FORMATS : XML_REPORT_WORD_FORMATS;
}

/** Export Data / Export Trend. The desktop dialog also offers an "All Files"
 *  wildcard, which it appends itself — a wildcard is meaningless as a format
 *  choice in the browser. */
export const EXPORT_DATA_FORMATS: ReportFormat[] = [
  { ext: "txt", labelId: "TIGER_EXPORT_FILTER_TEXT" },
  { ext: "xls", labelId: "TIGER_EXPORT_FILTER_EXCEL" },
];

/** Diagnostics window > Save Report (C++ diagnostics_shell_c::save_display).
 *  .xml / .fodt write query TEMPLATES rather than the displayed lines. */
export const DIAGNOSTICS_REPORT_FORMATS: ReportFormat[] = [
  { ext: "doc", labelId: "TIGER_DIAG_SAVE_FILTER_DOC" },
  { ext: "odt", labelId: "TIGER_DIAG_SAVE_FILTER_ODT" },
  { ext: "pdf", labelId: "TIGER_DIAG_SAVE_FILTER_PDF" },
  { ext: "txt", labelId: "TIGER_DIAG_SAVE_FILTER_TXT" },
  { ext: "xml", labelId: "TIGER_DIAG_SAVE_FILTER_XML" },
  { ext: "fodt", labelId: "TIGER_DIAG_SAVE_FILTER_FODT" },
];

/** The list as `@tauri-apps/plugin-dialog` `save({ filters })` wants it. */
export function toTauriFilters(formats: ReportFormat[]): { name: string; extensions: string[] }[] {
  return formats.map((f) => ({ name: getTigerResource(f.labelId), extensions: [f.ext] }));
}

/** The web dialog's row label: "Tiger Office Report (.doc)". */
export function formatLabel(format: ReportFormat): string {
  return `${getTigerResource(format.labelId)} (.${format.ext})`;
}

/** `<base>.<ext>`, with any extension the caller's default name already carries
 *  stripped (defaults are written as full filenames at the call sites). */
export function withExtension(base: string, ext: string): string {
  return `${base}.${ext}`;
}

/** Split a suggested filename into its base and (lower-case) extension. */
export function splitFilename(filename: string): { base: string; ext: string } {
  const dot = filename.lastIndexOf(".");
  if (dot <= 0) return { base: filename, ext: "" };
  return { base: filename.slice(0, dot), ext: filename.slice(dot + 1).toLowerCase() };
}
